import React from "react";
import { Label } from "../../../components/ui/label";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { ContactField } from "../hooks/useFieldDictionary";
import { IGNORE_TARGET, duplicateTargets, unmappedRequired } from "../lib/mappingValidation";

const IGNORE_TOKEN = "__ignore__";

export type MappingTableProps = {
  headers: string[];
  sampleRows: Array<Record<string, string>>;
  suggested: Record<string, string | null>;
  mapping: Record<string, string>;
  fields: ContactField[];
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
};

/**
 * Renders one row per CSV source header. Each row shows sample values (up to
 * first 3 non-empty), the auto-suggested target field (informational badge),
 * and a native <select> for the actual choice. The empty target (== ignore)
 * is modeled explicitly by the "— Ignore column —" option; the parent stores
 * ignored columns as an empty string (matching backend behavior).
 *
 * Uses a native select rather than the shadcn Select so that:
 *   - tests can drive it with `fireEvent.change`,
 *   - large CSV column counts remain responsive (no popper mount per row),
 *   - keyboard behavior + screen reader semantics are the browser's,
 *   - it works on mobile without a custom sheet.
 *
 * Validation aids surfaced here (client-side; server remains authoritative):
 *   - required target fields that are not yet mapped
 *   - target fields assigned to more than one source column
 * The Save button lives in the parent; this component only highlights
 * issues and marks conflicting selects as `aria-invalid`.
 */
export function HeadersMappingTable({
  headers,
  sampleRows,
  suggested,
  mapping,
  fields,
  onChange,
  disabled,
}: MappingTableProps) {
  const dupes = new Set(duplicateTargets(mapping));
  const missingRequired = unmappedRequired(mapping, fields);

  const setForHeader = (h: string, value: string) => {
    const target = value === IGNORE_TOKEN ? IGNORE_TARGET : value;
    onChange({ ...mapping, [h]: target });
  };

  const previewsFor = (h: string): string[] => {
    const out: string[] = [];
    for (const row of sampleRows) {
      const v = (row[h] ?? "").trim();
      if (v) out.push(v);
      if (out.length >= 3) break;
    }
    return out;
  };

  const selectClass =
    "block w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm shadow-sm focus:border-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 disabled:opacity-60";

  return (
    <div className="space-y-3" data-testid="headers-mapping-table">
      {missingRequired.length > 0 && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
          data-testid="mapping-missing-required"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Required field(s) not mapped</div>
            <div className="mt-0.5 text-rose-800/80">
              Map a column to:{" "}
              {missingRequired
                .map((code) => fields.find((f) => f.code === code)?.label ?? code)
                .join(", ")}
            </div>
          </div>
        </div>
      )}

      {dupes.size > 0 && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="mapping-duplicate-targets"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Duplicate target field(s)</div>
            <div className="mt-0.5 text-amber-800/80">
              The same target is assigned more than once:{" "}
              {[...dupes]
                .map((code) => fields.find((f) => f.code === code)?.label ?? code)
                .join(", ")}
            </div>
          </div>
        </div>
      )}

      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        {/* Desktop table (md+) */}
        <div className="hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Source column</th>
              <th className="text-left px-4 py-2.5 font-medium">Sample values</th>
              <th className="text-left px-4 py-2.5 font-medium w-64">Map to</th>
            </tr>
          </thead>
          <tbody>
            {headers.map((h) => {
              const current = mapping[h] ?? "";
              const selectValue = current === IGNORE_TARGET || !current ? IGNORE_TOKEN : current;
              const isDupe = !!current && dupes.has(current);
              const suggestion = suggested[h] ?? null;
              const previews = previewsFor(h);
              return (
                <tr
                  key={h}
                  className="border-t border-slate-200 align-top"
                  data-testid={`mapping-row-${h}`}
                >
                  <td className="px-4 py-3">
                    <div
                      className="font-medium text-slate-900 break-all"
                      data-testid={`mapping-source-${h}`}
                    >
                      {h}
                    </div>
                    {suggestion && (
                      <div
                        className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700"
                        data-testid={`mapping-suggestion-${h}`}
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Suggested:{" "}
                        {fields.find((f) => f.code === suggestion)?.label ?? suggestion}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {previews.length === 0 ? (
                      <span className="text-slate-400 text-xs">— empty —</span>
                    ) : (
                      <div className="space-y-0.5 min-w-0">
                        {previews.map((v, i) => (
                          <div
                            key={i}
                            className="text-slate-600 text-xs font-mono truncate"
                            title={v}
                          >
                            {v}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Label htmlFor={`sel-${h}`} className="sr-only">
                      Target field for column {h}
                    </Label>
                    <select
                      id={`sel-${h}`}
                      value={selectValue}
                      onChange={(e) => setForHeader(h, e.target.value)}
                      disabled={disabled}
                      aria-invalid={isDupe ? true : undefined}
                      className={`${selectClass} ${isDupe ? "border-amber-400" : ""}`}
                      data-testid={`mapping-target-${h}`}
                    >
                      <option value={IGNORE_TOKEN}>— Ignore column —</option>
                      {fields.map((f) => (
                        <option key={f.code} value={f.code}>
                          {f.label}
                          {f.required ? " *" : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>

        {/* Mobile stacked cards (< md) */}
        <ul
          className="md:hidden divide-y divide-slate-200"
          data-testid="mapping-cards"
          aria-label="CSV column mapping"
        >
          {headers.map((h) => {
            const current = mapping[h] ?? "";
            const selectValue = current === IGNORE_TARGET || !current ? IGNORE_TOKEN : current;
            const isDupe = !!current && dupes.has(current);
            const suggestion = suggested[h] ?? null;
            const previews = previewsFor(h);
            return (
              <li
                key={h}
                className="p-3"
                data-testid={`mapping-card-${h}`}
              >
                <div
                  className="font-medium text-slate-900 break-all text-sm"
                  data-testid={`mapping-source-mobile-${h}`}
                >
                  {h}
                </div>
                {suggestion && (
                  <div className="mt-1 inline-flex items-center gap-1 text-xs text-emerald-700">
                    <CheckCircle2 className="w-3 h-3" />
                    Suggested:{" "}
                    {fields.find((f) => f.code === suggestion)?.label ?? suggestion}
                  </div>
                )}
                {previews.length > 0 && (
                  <div className="mt-1.5 space-y-0.5 min-w-0">
                    {previews.map((v, i) => (
                      <div
                        key={i}
                        className="text-slate-600 text-xs font-mono truncate"
                        title={v}
                      >
                        {v}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-2">
                  <Label htmlFor={`sel-m-${h}`} className="text-xs text-slate-500">
                    Map to
                  </Label>
                  <select
                    id={`sel-m-${h}`}
                    value={selectValue}
                    onChange={(e) => setForHeader(h, e.target.value)}
                    disabled={disabled}
                    aria-invalid={isDupe ? true : undefined}
                    className={`${selectClass} mt-1 ${isDupe ? "border-amber-400" : ""}`}
                    data-testid={`mapping-target-mobile-${h}`}
                  >
                    <option value={IGNORE_TOKEN}>— Ignore column —</option>
                    {fields.map((f) => (
                      <option key={f.code} value={f.code}>
                        {f.label}
                        {f.required ? " *" : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="text-xs text-slate-500">
        <span className="text-rose-600">*</span> required. Columns set to “Ignore” are not imported.
      </div>
    </div>
  );
}
